import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { sendToWs } from '../../Websocket/actions';
import { clearDebugSnapshotRequest, requestDebugSnapshot } from '../../Websocket/debugSnapshotRequest';

const FULL_REQUEST_RETRY_MS = 5000;

const extractCltStatus = (websocket) => {
  const raw = websocket?.websocket?.cltstatus ?? websocket?.cltstatus ?? null;
  if (raw === null || raw === undefined || raw === '') return { data: raw, controlVariables: {}, tick: 0 };

  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = raw;
    }
  }

  if (parsed && typeof parsed === 'object' && Object.prototype.hasOwnProperty.call(parsed, 'map')) {
    return { data: parsed.map, controlVariables: parsed.controlVariables || {}, tick: Number(parsed.cycleData?.tick) || 0 };
  }
  return { data: parsed, controlVariables: {}, tick: 0 };
};

const useLockedCltStatus = (websocket, debugHud = false, archiveMode = false) => {
  const dispatch = useDispatch();
  const cltstatusValue = websocket?.websocket?.cltstatus;
  const websocketObject = websocket?.websocket?.websocketObject;
  const [data, setData] = useState(undefined);
  const [controlVariables, setControlVariables] = useState({});
  const [awaitingReload, setAwaitingReload] = useState(debugHud);
  const lastSnapshotRef = useRef(cltstatusValue);
  const wasArchiveModeRef = useRef(archiveMode);
  const websocketRef = useRef(websocket);
  websocketRef.current = websocket;

  const applySnapshot = useCallback(() => {
    const snapshot = extractCltStatus(websocket);
    if (snapshot.data === undefined) return false;
    setData(snapshot.data);
    setControlVariables(snapshot.controlVariables);
    return true;
  }, [websocket]);

  useEffect(() => {
    if (data !== undefined) return;
    if (!applySnapshot()) return;
    lastSnapshotRef.current = cltstatusValue;
  }, [applySnapshot, data, cltstatusValue]);

  useEffect(() => {
    const currentSnapshot = cltstatusValue;
    if (!currentSnapshot || currentSnapshot === lastSnapshotRef.current) return;
    lastSnapshotRef.current = currentSnapshot;
    if (archiveMode || awaitingReload || data === undefined) {
      if (applySnapshot() && awaitingReload) {
        setAwaitingReload(false);
      }
    }
  }, [archiveMode, cltstatusValue, applySnapshot, awaitingReload, data]);

  useEffect(() => {
    if (debugHud && wasArchiveModeRef.current && !archiveMode) {
      setAwaitingReload(true);
    }
    wasArchiveModeRef.current = archiveMode;
  }, [archiveMode, debugHud]);

  const requestSnapshot = useCallback(() => {
    if (websocketObject?.readyState !== 1) return;
    const { tick } = extractCltStatus(websocketRef.current);
    if (debugHud) requestDebugSnapshot();
    dispatch(sendToWs({ lastTick: debugHud ? 0 : tick }, 'request-clt-status-snapshot'));
  }, [debugHud, dispatch, websocketObject]);

  useEffect(
    () => () => {
      if (debugHud) clearDebugSnapshotRequest();
    },
    [debugHud]
  );

  useEffect(() => {
    if (!awaitingReload || archiveMode) return undefined;
    requestSnapshot();
    const interval = window.setInterval(requestSnapshot, FULL_REQUEST_RETRY_MS);
    return () => window.clearInterval(interval);
  }, [archiveMode, awaitingReload, requestSnapshot]);

  const reload = useCallback(() => setAwaitingReload(true), []);

  return { data, setData, reload, controlVariables };
};

export default useLockedCltStatus;
